// Step 1 — Draw Room.
//
// Port of stage's client/src/components/steps/Step1DrawRoom.tsx (the React
// component compiled into /var/www/kitchenos/tool-app/assets/main-y6nATn17.js).
// JSX structure was reconstructed from the `data-loc` source markers preserved
// in the bundle.
//
// Behaviour: parametric "+ Add" is the primary input. The user picks a tool,
// types a length in mm, picks a direction with the rotate buttons, clicks + Add.
// Click-to-place on the canvas is disabled (matches stage's pointerEvents:none
// on the canvas wrapper).
//
// 2026-08-10: the forced single path became one of FOUR equal-weight entry
// options (templates / freeform / upload / skip) — see EntryMode below. The
// templates path needs placed shapes to be draggable, so canvas.ts gained a
// `select`-tool move drag; the rest of it is still untouched.

import type { RoomDimensions, RoomMarker, Wall, LayoutType } from "../types";
import type { StepContext } from "./index";
import { authBarButtonHtml, wireAuthBarButton, openChatPopup } from "./index";
import { createCanvas, DEFAULT_WIDTH_MM } from "./canvas";
import type { CanvasHandle, CanvasTool } from "./canvas";

type Direction =
  | "right" | "down-right" | "down" | "down-left"
  | "left"  | "up-left"    | "up"   | "up-right";

const DIRECTIONS: Direction[] = [
  "right", "down-right", "down", "down-left",
  "left",  "up-left",    "up",   "up-right",
];

const DIRECTION_ANGLE: Record<Direction, number> = {
  "right": 0,
  "down-right": 45,
  "down": 90,
  "down-left": 135,
  "left": 180,
  "up-left": 225,
  "up": 270,
  "up-right": 315,
};

const DIRECTION_ARROW: Record<Direction, string> = {
  "right":      "→",
  "down-right": "↘",
  "down":       "↓",
  "down-left":  "↙",
  "left":       "←",
  "up-left":    "↖",
  "up":         "↑",
  "up-right":   "↗",
};

/** Unit vector per direction (screen-space: y grows downward). */
const DIRECTION_VECTOR: Record<Direction, { x: number; y: number }> = {
  "right":      { x:  1, y:  0 },
  "down-right": { x:  Math.SQRT1_2, y:  Math.SQRT1_2 },
  "down":       { x:  0, y:  1 },
  "down-left":  { x: -Math.SQRT1_2, y:  Math.SQRT1_2 },
  "left":       { x: -1, y:  0 },
  "up-left":    { x: -Math.SQRT1_2, y: -Math.SQRT1_2 },
  "up":         { x:  0, y: -1 },
  "up-right":   { x:  Math.SQRT1_2, y: -Math.SQRT1_2 },
};

/** Per-tool brand colours — verbatim from stage bundle (D2 = {...}). */
const TOOL_COLOUR: Record<CanvasTool, string> = {
  "wall":   "#9CA3AF",
  "door":   "#8B5E3C",
  "window": "#4A9BD9",
  "boiler": "#10B981",
  "select": "#9CA3AF",
};

interface Segment {
  type: Exclude<CanvasTool, "select">;
  lengthMm: number;
  direction: Direction;
}

const MIN_CEILING_MM = 1800;
const MAX_CEILING_MM = 9999;
const MIN_LENGTH_MM = 50;
// Sane bounds for an opening's width. A door under ~400mm or a window over
// ~6m is a typo, not a kitchen.
const MIN_OPENING_MM = 300;
const MAX_OPENING_MM = 6000;
const NOTES_PLACEHOLDER =
  "Add notes about your kitchen requirements, preferences, measurements, " +
  "or anything else relevant to your project.";

// ── Entry modes (2026-08-10 redesign) ────────────────────────────────────────
// The room step used to force everyone through the freeform planner. Most
// customers can't draw a floor plan, and the ones who can mostly don't want to
// — so drawing is now one of four equal-weight ways in, not the only way.
// Module-scoped (not in WizardState) so it survives Back/Next within a session
// but resets on reload, matching the wizard's in-memory-only contract.

type EntryMode = "templates" | "freeform" | "upload" | null;
let entryMode: EntryMode = null;

// ── Kitchen shape templates ──────────────────────────────────────────────────
// These describe the KITCHEN — where the cabinet runs go — not the room's
// footprint. That's what "L-shaped" means in the trade (units around a corner,
// usually in a plain rectangular room), and it's already how the rest of the
// platform defines it: see wizard_ai.js's layout_type prompt, ai_render.js
// (which counts runs and camera angles off it) and kitchen_layout.js.
//
// Corrected 2026-08-10 — the first cut of these templates drew L-shaped ROOM
// footprints, which was the odd one out across the whole codebase.
//
// Each tile therefore does two things: draws the walls the units run along for
// the customer to re-measure, and records `room.layoutType`. That capture is
// why the old "What shape is your kitchen?" card below Kitchen Notes could be
// removed — dropping it without this would silently degrade AI Render, which
// reads layout_type to decide how many runs to generate.
//
// Genuinely irregular rooms are still handled by "Draw it yourself".
//
// 2026-08-11 — per-wall lengths (Steve). Every wall of the chosen shape is
// listed and editable on its own (Wall 1, Wall 2, …) rather than as a single
// Room width / Room depth pair. The tile still records `room.layoutType`
// exactly as before, so AI Render's run/camera count is unaffected.
//
// 2026-08-12 — a template is the CABINET RUN ONLY; none of the four is a
// closed room (Steve). Single wall is one wall, a galley is two parallel walls
// facing each other, an L is two walls meeting at a 90° corner, and a U is
// three walls in an open horseshoe with nothing closing the open end. That is
// what the words mean in the trade, and it makes every wall length fully
// independent — there is no loop to close, so the closure solver that used to
// keep paired walls in step is gone with it.

/** One wall of a template: its two ends in template-local mm, before the plan
 *  origin is applied. */
type WallSeg = [[number, number], [number, number]];

interface RoomTemplate {
  id: string;
  name: string;
  /** What the customer sees under the name. */
  hint: string;
  /** The trade layout this records. Drives AI Render's run/camera count. */
  layoutType: LayoutType;
  /** Preview glyph — 48×40 viewBox. Shaded bars are the cabinet runs. */
  icon: string;
  /** Starting length of every wall, in mm. Wall 1 first; the array's length is
   *  how many walls this shape has. */
  seed: number[];
  /** Draw the walls for these lengths, in the same order as `seed`.
   *
   *  Every wall is drawn with the KITCHEN ON ITS LEFT (screen space, y grows
   *  downward: the inward normal of a wall drawn (dx, dy) is (−dy, dx)).
   *  canvas.ts reads that back off the geometry to decide which side of the
   *  wall to shade the cabinet run on, so the direction a wall is drawn in
   *  still matters even though the walls no longer form a loop. */
  walls: (len: number[]) => WallSeg[];
}

/** Shortest / longest a template wall may be. A 50mm wall is a typo, not a
 *  room, and 30m is past the point where anyone is fitting a kitchen. */
const MIN_TPL_WALL_MM = 300;
const MAX_TPL_WALL_MM = 30000;

/** How far apart a galley's two runs sit. The one measurement in any template
 *  that isn't a wall length: two facing walls need a separation to be drawn at
 *  all, and the shape is defined by its two wall lengths and nothing else.
 *  3m is a normal galley; the customer can drag and re-measure the walls, and
 *  anything genuinely unusual belongs in "Draw it yourself". */
const GALLEY_GAP_MM = 3000;

/** Tile glyph: the wall lines of the shape with the cabinet run shaded against
 *  each one, on the kitchen side. No room outline — the walls ARE the shape. */
function runIcon(walls: string, runs: string): string {
  return (
    `<path d="${walls}" fill="none" stroke="currentColor" stroke-width="1.5" ` +
    `stroke-linecap="round" stroke-linejoin="round" opacity="0.55"/>${runs}`
  );
}

/** A shaded cabinet run against a wall. */
function runBar(x: number, y: number, w: number, h: number): string {
  return `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="1" fill="currentColor"/>`;
}

const TEMPLATES: RoomTemplate[] = [
  {
    id: "single_wall",
    name: "Single wall",
    hint: "Units along one wall",
    layoutType: "single_wall",
    icon: runIcon("M6 17H42", runBar(6, 18, 36, 5)),
    seed: [4000],
    walls: ([a]) => [[[0, 0], [a!, 0]]],
  },
  {
    id: "galley",
    name: "Galley",
    hint: "Two runs facing each other",
    layoutType: "galley",
    icon: runIcon("M6 9H42M6 31H42", runBar(6, 10, 36, 5) + runBar(6, 26, 36, 5)),
    seed: [4000, 4000],
    // Wall 2 is drawn right-to-left so its kitchen side faces Wall 1's.
    walls: ([a, b]) => [
      [[0, 0], [a!, 0]],
      [[b!, GALLEY_GAP_MM], [0, GALLEY_GAP_MM]],
    ],
  },
  {
    id: "l_shaped",
    name: "L-shaped",
    hint: "Units around a corner",
    layoutType: "l_shape",
    icon: runIcon("M6 8H42V32", runBar(6, 9, 35, 5) + runBar(36, 9, 5, 23)),
    seed: [4000, 2600],
    // Wall 1 across the back, Wall 2 turning down the right at the corner.
    walls: ([a, b]) => [
      [[0, 0], [a!, 0]],
      [[a!, 0], [a!, b!]],
    ],
  },
  {
    id: "u_shaped",
    name: "U-shaped",
    hint: "Units on three sides",
    layoutType: "u_shape",
    icon: runIcon(
      "M6 32V8H42V32",
      runBar(7, 9, 34, 5) + runBar(7, 9, 5, 23) + runBar(36, 9, 5, 23),
    ),
    seed: [4000, 2600, 2600],
    // Wall 1 across the back, then the two returns. The left one is drawn
    // upward so the kitchen stays on its left; the open end stays open.
    walls: ([a, b, c]) => [
      [[0, 0], [a!, 0]],
      [[0, b!], [0, 0]],
      [[a!, 0], [a!, c!]],
    ],
  },
];

/** Is this a length we'll draw? Walls are independent now, so there is nothing
 *  to solve and nothing to invalidate — one field, one bounds check. */
function wallLengthIsValid(v: number): boolean {
  return Number.isFinite(v) && v >= MIN_TPL_WALL_MM && v <= MAX_TPL_WALL_MM;
}

/** Position a template's walls at `origin`.
 *
 *  Wall ids are POSITIONAL (`tpl-0`, `tpl-1`, …) and deliberately stable: the
 *  shape is regenerated on every keystroke while a wall is being re-measured,
 *  and openings are stored as {wallId, t} — a fresh random id per rebuild would
 *  throw the customer's doors and windows away as they typed. */
function wallsFromSegments(segs: WallSeg[], origin: { x: number; y: number }): Wall[] {
  return segs.map(([[x1, y1], [x2, y2]], i) => ({
    id: `tpl-${i}`,
    x1: x1 + origin.x, y1: y1 + origin.y,
    x2: x2 + origin.x, y2: y2 + origin.y,
  }));
}

/** Length of a wall in mm. */
function wallLength(w: Wall): number {
  return Math.hypot(w.x2 - w.x1, w.y2 - w.y1);
}

/** Top-left of the current plan, so re-measuring a template keeps it where the
 *  customer dragged it instead of snapping back to the origin. */
function planOrigin(walls: Wall[]): { x: number; y: number } {
  if (walls.length === 0) return { x: 0, y: 0 };
  return {
    x: Math.min(...walls.map((w) => Math.min(w.x1, w.x2))),
    y: Math.min(...walls.map((w) => Math.min(w.y1, w.y2))),
  };
}

/** The island Yes/No control. Rendered twice — once in the Doors & Windows
 *  toolbar row, once in the standalone card for the paths that have no toolbar —
 *  with both copies wired to the same state and kept in step. */
function islandToggleHtml(room: RoomDimensions): string {
  return `
    <span class="wiz-island-row__label">Would you like an island?</span>
    <span class="wiz-island-toggle" data-role="island-toggle">
      <button type="button" class="wiz-island-btn${room.hasIsland === true ? " is-selected" : ""}" data-island="yes">Yes</button>
      <button type="button" class="wiz-island-btn${room.hasIsland === false ? " is-selected" : ""}" data-island="no">No</button>
    </span>`;
}

export function mountStep1(host: HTMLElement, ctx: StepContext): void {
  // Returning to the step with work already done — jump straight back to the
  // relevant panel rather than re-asking how they'd like to start.
  if (entryMode === null) {
    if (ctx.state.room.walls.length > 0) entryMode = "freeform";
    else if (ctx.state.project.planUploads.length > 0) entryMode = "upload";
  }
  host.innerHTML = `
    <div class="wiz-step1">
      <!-- Four equal-weight ways in. None is the "real" one — a customer who
           picks Skip is as welcome as one who draws their room to the mm. -->
      <section class="wiz-entry">
        <h2 class="wiz-entry__title">How would you like to start?</h2>
        <p class="wiz-entry__sub">Any of these is fine — you can change your mind later, and none of it is required.</p>
        <div class="wiz-entry__grid" data-role="entry-grid">
          <button type="button" class="wiz-entry__tile" data-entry="templates">
            <svg viewBox="0 0 48 40" width="44" height="36" aria-hidden="true"><path d="M5 6h20v12h18v16H5z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>
            <span class="wiz-entry__name">Kitchen shape templates</span>
            <span class="wiz-entry__hint">Pick your kitchen shape and adjust the room size</span>
          </button>
          <button type="button" class="wiz-entry__tile" data-entry="freeform">
            <svg viewBox="0 0 48 40" width="44" height="36" aria-hidden="true"><path d="M6 34V10l12-4 12 6 12-3v25z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><circle cx="18" cy="6" r="2" fill="currentColor"/><circle cx="30" cy="12" r="2" fill="currentColor"/></svg>
            <span class="wiz-entry__name">Draw it yourself</span>
            <span class="wiz-entry__hint">Walls, doors and windows, measured to the millimetre</span>
          </button>
          <button type="button" class="wiz-entry__tile" data-entry="upload">
            <svg viewBox="0 0 48 40" width="44" height="36" aria-hidden="true"><path d="M40 26v5a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2v-5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M24 25V7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M17 14l7-7 7 7" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            <span class="wiz-entry__name">Upload a plan or photo</span>
            <span class="wiz-entry__hint">Any format — an architect's plan, a sketch, or a snap</span>
          </button>
          <button type="button" class="wiz-entry__tile" data-entry="skip">
            <svg viewBox="0 0 48 40" width="44" height="36" aria-hidden="true"><path d="M14 12l10 8-10 8M26 12l10 8-10 8" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            <span class="wiz-entry__name">Skip this step</span>
            <span class="wiz-entry__hint">We'll measure up with you later — nothing needed now</span>
          </button>
        </div>
      </section>

      <!-- Templates panel -->
      <section class="wiz-panel" data-panel="templates" hidden>
        <div class="wiz-tpl-grid" data-role="tpl-grid">
          ${TEMPLATES.map((t) => `
            <button type="button" class="wiz-tpl" data-tpl="${t.id}">
              <svg viewBox="0 0 48 40" width="48" height="40" aria-hidden="true">${t.icon}</svg>
              <span class="wiz-tpl__name">${t.name}</span>
              <span class="wiz-tpl__hint">${t.hint}</span>
            </button>
          `).join("")}
        </div>
        <div class="wiz-tpl-params" data-role="tpl-params" hidden></div>

        <!-- Doors and windows on the template's walls. Tap a tool, tap a wall
             to drop one, then drag it along that wall or retype its width. -->
        <div class="wiz-openings" data-role="openings" hidden>
          <div class="wiz-openings__tools">
            <span class="wiz-openings__label">Doors &amp; windows</span>
            <button type="button" class="wiz-openings__tool is-active" data-open-tool="select">Move plan</button>
            <button type="button" class="wiz-openings__tool" data-open-tool="door">+ Door</button>
            <button type="button" class="wiz-openings__tool" data-open-tool="window">+ Window</button>
            <!-- Exact placement — the same mm / + Add / Undo controls as the
                 freeform toolbar, so an opening can be measured in rather than
                 tapped in. Wall picker because the measurement is taken from
                 that wall's starting corner, clockwise. -->
            <span class="wiz-openings__place">
              <select class="wiz-openings__wall" data-role="open-wall" aria-label="Wall to place the opening on"></select>
              <span class="wiz-openings__field">
                <input class="wiz-num" data-role="open-offset" type="number" inputmode="numeric"
                       min="0" max="${MAX_TPL_WALL_MM}" step="10" placeholder="mm"
                       aria-label="Distance from the start of the wall, in millimetres" />
                <span class="wiz-openings__unit">from corner</span>
              </span>
              <button type="button" class="wiz-openings__add" data-action="open-add" disabled>+ Add</button>
              <button type="button" class="wiz-openings__undo" data-action="open-undo" disabled title="Undo last door or window">↶ Undo</button>
            </span>
            <!-- The island question lives in this row's spare space (moved here
                 2026-08-11 from the card below the canvas). The standalone card
                 is still the home for it on the freeform/upload paths, where
                 this toolbar doesn't exist — see syncIslandPlacement. -->
            <span class="wiz-island-row wiz-island-row--inline">
              ${islandToggleHtml(ctx.state.room)}
            </span>
          </div>
          <p class="wiz-openings__hint" data-role="openings-hint"></p>
          <div class="wiz-openings__list" data-role="openings-list"></div>
        </div>
        <p class="wiz-tpl-hint" data-role="tpl-hint" hidden>
          Each wall is numbered on the plan — set its length above and the drawing follows.
          These are the walls your units run along, measured independently, not the whole room.
          Drag the plan on the canvas to reposition it.
          Need a shape that isn't here? Switch to <button type="button" class="wiz-linkbtn" data-entry-jump="freeform">drawing it yourself</button>.
        </p>
      </section>

      <!-- Row 1: tool buttons + ceiling -->
      <div class="wiz-panel" data-panel="freeform" hidden>
      <div class="wiz-row1">
        <div class="wiz-tool-row" data-role="tools">
          ${(["wall", "door", "window", "boiler"] as const).map((t) => `
            <button type="button" class="wiz-tool" data-tool="${t}">${t}</button>
          `).join("")}
        </div>
        <div class="wiz-ceiling">
          <label class="wiz-ceiling__label">Ceiling</label>
          <input
            class="wiz-num wiz-num--ceiling"
            id="ceiling-height-input"
            data-role="ceiling"
            type="number"
            inputmode="numeric"
            placeholder="mm"
            max="9999"
            value="${ctx.state.room.ceilingHeightMm}"
          />
        </div>
      </div>

      <!-- Row 2: length + direction + rotate + Add + Undo + Clear -->
      <div class="wiz-row2">
        <div class="wiz-row2__main">
          <input
            class="wiz-num wiz-num--length"
            id="segment-length-input"
            data-role="length"
            type="number"
            inputmode="numeric"
            placeholder="mm"
            max="9999999"
          />
          <div class="wiz-direction" data-role="direction" aria-label="Direction"></div>
          <button type="button" class="wiz-rot" data-action="rotate-ccw" title="Rotate anticlockwise" aria-label="Rotate anticlockwise">↺</button>
          <button type="button" class="wiz-rot" data-action="rotate-cw"  title="Rotate clockwise"     aria-label="Rotate clockwise">↻</button>
          <button type="button" class="wiz-add"  data-action="add" disabled>+ Add</button>
          <button type="button" class="wiz-undo" data-action="undo" disabled title="Undo last segment">↶ Undo</button>
          <button type="button" class="wiz-clear" data-action="clear" title="Clear all segments">✕ Clear</button>
        </div>
        <div class="wiz-segments" data-role="segments" hidden></div>
      </div>
      </div>

      <!-- Upload zone: attach an existing plan / photos instead of (or as well
           as) drawing. Any format accepted. -->
      <div class="wiz-panel" data-panel="upload" hidden>
        <div class="wiz-upload">
          <label class="wiz-upload__drop" data-role="upload-drop">
            <input type="file" multiple data-role="upload-input" class="wiz-upload__input" aria-label="Upload a floor plan or photos" />
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            <span class="wiz-upload__text">Already have a floor plan or photos? Upload them here — any format accepted.</span>
          </label>
          <div class="wiz-upload__list" data-role="upload-list"></div>
        </div>
      </div>

      <!-- Canvas — shared by the templates and freeform panels. -->
      <div class="wiz-panel" data-panel="canvas" hidden>
        <div class="wiz-canvas-wrap-outer" data-role="canvas-host"></div>
        <div class="wiz-counts" data-role="counts"></div>
      </div>

      <!-- Kitchen Notes card -->
      <div class="wiz-notes-card">
        <div class="wiz-notes-card__header">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="wiz-notes-card__icon">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
          </svg>
          <h3 class="wiz-notes-card__title">Kitchen Notes</h3>
        </div>
        <textarea
          class="wiz-notes-card__textarea"
          data-role="notes"
          placeholder="${NOTES_PLACEHOLDER}"
        >${escapeText(ctx.state.room.notes)}</textarea>
        <p class="wiz-notes-card__hint">Notes are saved with the floor plan and linked to the customer record.</p>
      </div>

      <!-- Island (feeds the AI render camera angles).
           The "What shape is your kitchen?" tiles that used to head this card
           were removed 2026-08-10: the Kitchen shape templates above now
           capture layoutType directly, so asking again here was redundant.
           The island question is NOT redundant — has_island is separate data
           that no template records, so it stays.
           2026-08-11: on the templates path the same question now sits in the
           Doors & Windows toolbar row and this card hides itself. It stays in
           the DOM because freeform/upload have no such row, and hiding it
           outright would silently drop has_island for those customers. -->

      <div class="wiz-layout-card" data-role="island-card">
        <div class="wiz-island-row">
          ${islandToggleHtml(ctx.state.room)}
        </div>
      </div>

      <!-- How to Measure Your Room banner -->
      <section class="wiz-howto">
        <div class="wiz-howto__icon">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
        </div>
        <div class="wiz-howto__body">
          <h3 class="wiz-howto__title">How to Measure Your Room</h3>
          <div class="wiz-howto__paras">
            <p>Start in a corner of your room and, moving in a <strong>clockwise direction</strong>, measure each wall, door and window. Include the architrave as part of the total width of doors and windows.</p>
            <p>We'll import your plan into our design software and create a <strong>3D design</strong> based on the options you choose in the next step.</p>
            <p class="wiz-howto__contact">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              <span>Need help with your design? <a href="/retailer-inbox" class="wiz-howto__link">Message your kitchen specialist</a> directly through the app — they're always happy to help.</span>
            </p>
          </div>
        </div>
      </section>

      <!-- Floating bottom nav (Save / Chat / Login / Next) — ported from
           the stage React app's desktop nav extras + mobile bottom bar. -->
      <div class="wiz-bottombar">
        <button type="button" class="wiz-bb-btn" data-action="save" title="Save your progress">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
          <span>Save</span>
        </button>
        <button type="button" class="wiz-bb-btn" data-action="chat" title="Chat with us">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          <span>Chat</span>
        </button>
        ${authBarButtonHtml(ctx.state)}
        <button type="button" class="wiz-bb-next" data-action="next">Next: Kitchen Style →</button>
      </div>
    </div>
  `;

  const toolsEl = host.querySelector<HTMLElement>("[data-role=tools]")!;
  const ceilingInput = host.querySelector<HTMLInputElement>("[data-role=ceiling]")!;
  const lengthInput = host.querySelector<HTMLInputElement>("[data-role=length]")!;
  const directionEl = host.querySelector<HTMLElement>("[data-role=direction]")!;
  const segmentsEl = host.querySelector<HTMLElement>("[data-role=segments]")!;
  const canvasHost = host.querySelector<HTMLElement>("[data-role=canvas-host]")!;
  const countsEl = host.querySelector<HTMLElement>("[data-role=counts]")!;
  const notesEl = host.querySelector<HTMLTextAreaElement>("[data-role=notes]")!;
  const addBtn = host.querySelector<HTMLButtonElement>('[data-action=add]')!;
  const undoBtn = host.querySelector<HTMLButtonElement>('[data-action=undo]')!;
  const clearBtn = host.querySelector<HTMLButtonElement>('[data-action=clear]')!;
  const rotCcw = host.querySelector<HTMLButtonElement>('[data-action=rotate-ccw]')!;
  const rotCw  = host.querySelector<HTMLButtonElement>('[data-action=rotate-cw]')!;
  const nextBtn = host.querySelector<HTMLButtonElement>('[data-action=next]')!;
  const uploadInput = host.querySelector<HTMLInputElement>('[data-role=upload-input]')!;
  const uploadList = host.querySelector<HTMLElement>('[data-role=upload-list]')!;
  const entryGrid = host.querySelector<HTMLElement>('[data-role=entry-grid]')!;
  const tplGrid = host.querySelector<HTMLElement>('[data-role=tpl-grid]')!;
  const tplParams = host.querySelector<HTMLElement>('[data-role=tpl-params]')!;
  const tplHint = host.querySelector<HTMLElement>('[data-role=tpl-hint]')!;
  const uploadDrop = host.querySelector<HTMLElement>('[data-role=upload-drop]')!;
  const islandCard = host.querySelector<HTMLElement>('[data-role=island-card]')!;
  const openWallSel = host.querySelector<HTMLSelectElement>('[data-role=open-wall]')!;
  const openOffset = host.querySelector<HTMLInputElement>('[data-role=open-offset]')!;
  const openAddBtn = host.querySelector<HTMLButtonElement>('[data-action=open-add]')!;
  const openUndoBtn = host.querySelector<HTMLButtonElement>('[data-action=open-undo]')!;

  // Island → room state; feeds the AI render camera angles. The sibling
  // layout-type picker was removed 2026-08-10 (Kitchen shape templates set it).
  // Two copies of the control exist (toolbar row + standalone card), so the
  // click is handled once and every copy is repainted from the new value.
  for (const box of host.querySelectorAll<HTMLElement>("[data-role=island-toggle]")) {
    box.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-island]");
      if (!btn) return;
      const wantsIsland = btn.dataset.island === "yes";
      ctx.setState({ ...ctx.state, room: { ...ctx.state.room, hasIsland: wantsIsland } });
      for (const b of host.querySelectorAll<HTMLButtonElement>("[data-island]")) {
        b.classList.toggle("is-selected", (b.dataset.island === "yes") === wantsIsland);
      }
    });
  }

  /** Only ever show the island question once: the toolbar copy wins whenever
   *  that toolbar is on screen. */
  function syncIslandPlacement(): void {
    islandCard.hidden = !openingsBox.hidden;
  }

  // ── Local UI state ──
  let currentTool: Exclude<CanvasTool, "select"> = "wall";
  let directionIdx = 0;
  const segments: Segment[] = [];

  // The stage tool behaves like drawing with a pen. This point is the only
  // source of truth for where the next element begins. Every + Add starts here,
  // then advances the pen to the new endpoint.
  let pen = seedPenFromRoom(ctx.state.room);

  // ── Canvas (pointer events disabled in stage; we mirror that) ──
  const canvas: CanvasHandle = createCanvas(
    canvasHost,
    () => ctx.state.room,
    {
      commit: (next) => {
        ctx.setState({ ...ctx.state, room: next });
        canvas.rerender();
        // Placing or sliding an opening on the canvas has to keep the
        // openings list (and its width inputs) in step.
        renderOpenings();
        renderCounts();
      },
    },
  );

  // ── Render helpers ──
  function setTool(t: Exclude<CanvasTool, "select">): void {
    currentTool = t;
    canvas.setTool(t);
    toolsEl.querySelectorAll<HTMLButtonElement>("[data-tool]").forEach((b) => {
      const on = b.dataset.tool === t;
      b.classList.toggle("wiz-tool--active", on);
      b.style.backgroundColor = on ? "var(--brand-primary)" : "";
      b.style.color = on ? "var(--brand-text)" : "";
      b.style.borderColor = on ? "var(--brand-primary)" : "";
    });
    syncAddButton();
    setTimeout(() => lengthInput.focus(), 0);
  }

  function renderDirection(): void {
    const dir = DIRECTIONS[directionIdx]!;
    directionEl.textContent = DIRECTION_ARROW[dir];
    updateCanvasCursor();
  }

  function updateCanvasCursor(): void {
    const dir = DIRECTIONS[directionIdx]!;
    canvas.setCursor(pen, DIRECTION_ANGLE[dir]);
  }

  function renderSegments(): void {
    if (segments.length === 0) {
      segmentsEl.hidden = true;
      segmentsEl.innerHTML = "";
      return;
    }
    segmentsEl.hidden = false;
    segmentsEl.innerHTML = segments.map((s) => `
      <span class="wiz-seg" style="background-color:${TOOL_COLOUR[s.type]}">
        <span class="wiz-seg__type">${s.type[0]!.toUpperCase()}</span>
        <span class="wiz-seg__len">${s.lengthMm}</span>
        <span class="wiz-seg__dir">${DIRECTION_ARROW[s.direction]}</span>
      </span>
    `).join("");
  }

  function renderCounts(): void {
    countsEl.textContent = segments.length > 0
      ? `${segments.length} segment${segments.length === 1 ? "" : "s"} added`
      : "";
  }

  function syncAddButton(): void {
    const v = Number(lengthInput.value);
    const disabled = !lengthInput.value || !isFinite(v) || v < MIN_LENGTH_MM;
    addBtn.disabled = disabled;
    addBtn.style.backgroundColor = disabled ? "" : TOOL_COLOUR[currentTool];
    addBtn.style.opacity = disabled ? "0.5" : "1";
  }

  function syncUndoButton(): void {
    undoBtn.disabled = segments.length === 0;
  }

  // ── Wire row 1 ──
  toolsEl.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-tool]");
    if (!b) return;
    setTool(b.dataset.tool as Exclude<CanvasTool, "select">);
  });

  ceilingInput.addEventListener("input", () => {
    const raw = ceilingInput.value;
    if (raw.length > 4) {
      ceilingInput.value = raw.slice(0, 4);
    }
    const v = Number(ceilingInput.value);
    if (!isFinite(v)) return;
    const clamped = Math.min(MAX_CEILING_MM, Math.max(MIN_CEILING_MM, v));
    ctx.setState({ ...ctx.state, room: { ...ctx.state.room, ceilingHeightMm: clamped } });
  });

  // ── Wire row 2 ──
  lengthInput.addEventListener("input", () => {
    if (lengthInput.value.length > 7) lengthInput.value = lengthInput.value.slice(0, 7);
    syncAddButton();
  });
  lengthInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !addBtn.disabled) {
      e.preventDefault();
      addBtn.click();
    }
  });

  rotCcw.addEventListener("click", () => {
    directionIdx = (directionIdx - 1 + DIRECTIONS.length) % DIRECTIONS.length;
    renderDirection();
  });
  rotCw.addEventListener("click", () => {
    directionIdx = (directionIdx + 1) % DIRECTIONS.length;
    renderDirection();
  });

  addBtn.addEventListener("click", () => {
    const v = Number(lengthInput.value);
    if (!isFinite(v) || v < MIN_LENGTH_MM) return;
    const dir = DIRECTIONS[directionIdx]!;
    const vec = DIRECTION_VECTOR[dir];
    const room = ctx.state.room;
    const start = { ...pen };
    const end = {
      x: Math.round(start.x + vec.x * v),
      y: Math.round(start.y + vec.y * v),
    };

    // Every tool creates a chained segment from the current pen position to the
    // new endpoint. Doors/windows/boilers also add a marker on that new segment
    // so their colour/symbol renders correctly without reusing the previous wall.
    const wall: Wall = {
      id: makeId(),
      x1: start.x,
      y1: start.y,
      x2: end.x,
      y2: end.y,
    };
    const next: RoomDimensions = { ...room, walls: [...room.walls, wall] };

    if (currentTool !== "wall") {
      const marker: RoomMarker = {
        id: makeId(),
        kind: currentTool,
        wallId: wall.id,
        t: 0.5,
        widthMm: currentTool === "boiler" ? DEFAULT_WIDTH_MM.boiler : v,
      };
      if (currentTool === "door")   next.doors   = [...room.doors,   marker];
      if (currentTool === "window") next.windows = [...room.windows, marker];
      if (currentTool === "boiler") next.boilers = [...room.boilers, marker];
    }

    pen = end;
    ctx.setState({ ...ctx.state, room: next });

    segments.push({ type: currentTool, lengthMm: v, direction: dir });
    updateCanvasCursor();
    canvas.rerender();
    renderSegments();
    renderCounts();
    syncUndoButton();
    lengthInput.value = "";
    syncAddButton();
    lengthInput.focus();
  });

  undoBtn.addEventListener("click", () => {
    const last = segments.pop();
    if (!last) return;
    const room = ctx.state.room;
    const next: RoomDimensions = { ...room };
    // Each added element owns the most recent wall segment. Undo removes that
    // segment and, for non-wall tools, its matching marker.
    next.walls = room.walls.slice(0, -1);
    if (last.type === "door") {
      next.doors = room.doors.slice(0, -1);
    } else if (last.type === "window") {
      next.windows = room.windows.slice(0, -1);
    } else if (last.type === "boiler") {
      next.boilers = room.boilers.slice(0, -1);
    }
    pen = seedPenFromRoom(next);
    ctx.setState({ ...ctx.state, room: next });
    updateCanvasCursor();
    canvas.rerender();
    renderSegments();
    renderCounts();
    syncUndoButton();
  });

  clearBtn.addEventListener("click", () => {
    if (segments.length === 0) return;
    if (!window.confirm("Clear all segments?")) return;
    segments.length = 0;
    pen = { x: 0, y: 0 };
    const room = ctx.state.room;
    ctx.setState({
      ...ctx.state,
      room: {
        walls: [], doors: [], windows: [], boilers: [],
        ceilingHeightMm: room.ceilingHeightMm,
        notes: room.notes,
      },
    });
    updateCanvasCursor();
    canvas.rerender();
    renderSegments();
    renderCounts();
    syncUndoButton();
  });

  // ── Kitchen Notes ──
  notesEl.addEventListener("input", () => {
    ctx.setState({ ...ctx.state, room: { ...ctx.state.room, notes: notesEl.value } });
  });

  // ── Floor-plan / photo uploads ──
  const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // 10 MB each
  const MAX_UPLOADS = 8;

  function renderUploads(): void {
    const files = ctx.state.project.planUploads ?? [];
    if (files.length === 0) {
      uploadList.hidden = true;
      uploadList.innerHTML = "";
      return;
    }
    uploadList.hidden = false;
    uploadList.innerHTML = files
      .map((f, i) => {
        const isImg = /^data:image\//i.test(f.dataUrl);
        const thumb = isImg
          ? `<img class="wiz-upload__thumb" src="${escapeAttr(f.dataUrl)}" alt="" />`
          : `<span class="wiz-upload__thumb wiz-upload__thumb--file" aria-hidden="true">📄</span>`;
        return `
          <span class="wiz-upload__chip">
            ${thumb}
            <span class="wiz-upload__chip-name">${escapeText(f.name)}</span>
            <button type="button" class="wiz-upload__chip-remove" data-remove="${i}" aria-label="Remove ${escapeAttr(f.name)}">×</button>
          </span>`;
      })
      .join("");
  }

  function addUploads(fileList: FileList | null): void {
    if (!fileList || fileList.length === 0) return;
    const existing = ctx.state.project.planUploads ?? [];
    const slots = MAX_UPLOADS - existing.length;
    if (slots <= 0) {
      toast(uploadDrop, `Up to ${MAX_UPLOADS} files`);
      return;
    }
    const chosen = Array.from(fileList).slice(0, slots);
    let pending = chosen.length;
    const collected: typeof existing = [];
    for (const file of chosen) {
      if (file.size > MAX_UPLOAD_BYTES) {
        pending -= 1;
        continue;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = String(reader.result || "");
        if (dataUrl) {
          collected.push({ name: file.name, dataUrl, sizeBytes: file.size });
        }
        pending -= 1;
        if (pending === 0) commitUploads(collected);
      };
      reader.onerror = () => {
        pending -= 1;
        if (pending === 0) commitUploads(collected);
      };
      reader.readAsDataURL(file);
    }
    if (pending === 0) commitUploads(collected);
  }

  function commitUploads(added: typeof ctx.state.project.planUploads): void {
    if (added.length === 0) return;
    const next = [...(ctx.state.project.planUploads ?? []), ...added];
    ctx.setState({
      ...ctx.state,
      project: { ...ctx.state.project, planUploads: next },
    });
    renderUploads();
  }

  uploadInput.addEventListener("change", () => {
    addUploads(uploadInput.files);
    uploadInput.value = ""; // allow re-selecting the same file
  });

  uploadList.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-remove]");
    if (!btn) return;
    const idx = Number(btn.dataset.remove);
    const files = ctx.state.project.planUploads ?? [];
    const next = files.filter((_, i) => i !== idx);
    ctx.setState({
      ...ctx.state,
      project: { ...ctx.state.project, planUploads: next },
    });
    renderUploads();
  });

  // ── Entry modes ──
  // Skip is deliberately not a mode: it's an immediate exit. It sets the flag
  // the retailer sees ("customer couldn't measure — arrange a visit") and moves
  // straight on. No validation runs, because there is nothing to validate.
  const applyEntryMode = (): void => {
    for (const panel of host.querySelectorAll<HTMLElement>("[data-panel]")) {
      const name = panel.getAttribute("data-panel");
      panel.hidden =
        name === "canvas"
          ? entryMode !== "templates" && entryMode !== "freeform"
          : name !== entryMode;
    }
    for (const tile of entryGrid.querySelectorAll<HTMLButtonElement>("[data-entry]")) {
      const on = tile.getAttribute("data-entry") === entryMode;
      tile.classList.toggle("is-selected", on);
      tile.setAttribute("aria-pressed", String(on));
    }
    // The move tool only makes sense once there's a plan to move.
    const movable = entryMode === "templates";
    canvas.setTool(movable ? "select" : "wall");
    // The canvas wrapper is pointer-events:none by default (ported verbatim
    // from stage, where input was parametric-only). Dragging a template needs
    // it back — but only in that mode, so the freeform planner keeps behaving
    // exactly as it did.
    canvasHost.classList.toggle("is-movable", movable);
    syncIslandPlacement();
  };

  const chooseEntry = (mode: string | null): void => {
    if (mode === "skip") {
      ctx.setState({
        ...ctx.state,
        project: { ...ctx.state.project, step1Skipped: true },
      });
      ctx.goTo(1);
      return;
    }
    if (mode !== "templates" && mode !== "freeform" && mode !== "upload") return;
    if (mode === entryMode) return;

    // Leaving the templates panel takes the placed template WITH it — otherwise
    // switching to "Draw it yourself" starts you on top of the old shape.
    // Surgical on purpose: only walls this file generated (`tpl-` ids) are
    // removed, so a hand-drawn plan is never destroyed by a stray tile click.
    // Markers are wall-relative, so any pinned to those walls go too.
    if (entryMode === "templates") clearTemplateWalls();

    entryMode = mode;
    applyEntryMode();
  };

  /** Drop template-generated geometry and reset the picker back to neutral. */
  const clearTemplateWalls = (): void => {
    const room = ctx.state.room;
    const keptWalls = room.walls.filter((w) => !w.id.startsWith("tpl-"));
    if (keptWalls.length !== room.walls.length) {
      const liveIds = new Set(keptWalls.map((w) => w.id));
      const keep = (m: RoomMarker): boolean => liveIds.has(m.wallId);
      ctx.setState({
        ...ctx.state,
        room: {
          ...room,
          walls: keptWalls,
          doors: room.doors.filter(keep),
          windows: room.windows.filter(keep),
          boilers: room.boilers.filter(keep),
        },
      });
      canvas.rerender();
      renderCounts();
      renderWallOptions();
      renderOpenings();
    }
    activeTemplate = null;
    tplLengths = [];
    for (const b of tplGrid.querySelectorAll<HTMLButtonElement>("[data-tpl]")) {
      b.classList.remove("is-selected");
      b.setAttribute("aria-pressed", "false");
    }
    renderTemplateParams();
  };

  entryGrid.addEventListener("click", (e) => {
    const tile = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-entry]");
    if (tile) chooseEntry(tile.getAttribute("data-entry"));
  });
  host.querySelector<HTMLButtonElement>("[data-entry-jump=freeform]")
    ?.addEventListener("click", () => chooseEntry("freeform"));

  // ── Room shape templates ──
  // Wall lengths live here rather than in WizardState: once placed, the
  // template IS just walls, and the numbers are only a convenience for
  // regenerating it. One entry per wall, in Wall 1, Wall 2, … order.
  let activeTemplate: RoomTemplate | null = null;
  let tplLengths: number[] = [];

  /** Regenerate the shape from `tplLengths`.
   *
   *  `keepOpenings` is the difference between picking a tile (a new shape, so
   *  nothing to keep) and re-measuring a wall of the shape already on screen —
   *  which happens on every keystroke, and must not cost the customer the doors
   *  and windows they've already positioned. */
  const placeTemplate = (keepOpenings: boolean): void => {
    if (!activeTemplate) return;
    const room = ctx.state.room;
    // Keep it wherever the customer last dragged it.
    const origin = planOrigin(room.walls);
    const walls = wallsFromSegments(activeTemplate.walls(tplLengths), origin);
    const oldById = new Map(room.walls.map((w) => [w.id, w]));
    const newById = new Map(walls.map((w) => [w.id, w]));

    // Openings are carried by the distance they were MEASURED at — from their
    // wall's starting corner — not by their fraction along it. Lengthen a wall
    // and a door typed in at 1200mm is still at 1200mm, which is the whole
    // point of measuring it in rather than dragging it.
    const carry = (list: RoomMarker[]): RoomMarker[] => {
      if (!keepOpenings) return [];
      const out: RoomMarker[] = [];
      for (const m of list) {
        const before = oldById.get(m.wallId);
        const after = newById.get(m.wallId);
        if (!before || !after) continue;            // wall no longer exists
        const oldLen = wallLength(before);
        const newLen = wallLength(after);
        if (oldLen < 1 || newLen < 1) continue;
        const near = Math.max(0, m.t * oldLen - m.widthMm / 2);
        const maxNear = newLen - m.widthMm;
        if (maxNear < 0) continue;                  // wall too short for it now
        out.push({ ...m, t: (Math.min(near, maxNear) + m.widthMm / 2) / newLen });
      }
      return out;
    };

    ctx.setState({
      ...ctx.state,
      room: {
        ...room,
        walls,
        doors: carry(room.doors), windows: carry(room.windows), boilers: carry(room.boilers),
        // The tile IS the kitchen-shape answer — this is what the removed
        // "What shape is your kitchen?" card used to capture, and what
        // AI Render reads to decide run count and camera angles.
        layoutType: activeTemplate.layoutType,
      },
    });
    canvas.rerender();
    renderCounts();
    renderWallOptions();
    renderOpenings();
  };

  const renderTemplateParams = (): void => {
    if (!activeTemplate) {
      tplParams.hidden = true; tplHint.hidden = true; openingsBox.hidden = true;
      syncIslandPlacement();
      return;
    }
    // Every wall of the shape, listed in the order the plan draws them, each
    // with its own length. The numbers on the canvas match.
    tplParams.innerHTML = tplLengths
      .map(
        (len, i) => `
        <label class="wiz-tpl-param">
          <span class="wiz-tpl-param__label">Wall ${i + 1}</span>
          <span class="wiz-tpl-param__field">
            <input class="wiz-num" type="number" inputmode="numeric" min="${MIN_TPL_WALL_MM}"
                   max="${MAX_TPL_WALL_MM}" step="10" data-tpl-wall="${i}"
                   value="${Math.round(len)}" />
            <span class="wiz-tpl-param__unit">mm</span>
          </span>
        </label>`,
      )
      .join("");
    tplParams.hidden = false;
    tplHint.hidden = false;
    openingsBox.hidden = false;
    syncIslandPlacement();
  };

  tplGrid.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-tpl]");
    if (!btn) return;
    const tpl = TEMPLATES.find((t) => t.id === btn.getAttribute("data-tpl"));
    if (!tpl) return;
    activeTemplate = tpl;
    tplLengths = [...tpl.seed];
    for (const b of tplGrid.querySelectorAll<HTMLButtonElement>("[data-tpl]")) {
      b.classList.toggle("is-selected", b === btn);
      b.setAttribute("aria-pressed", String(b === btn));
    }
    renderTemplateParams();
    placeTemplate(false);
  });

  // ── Doors & windows on template walls ──
  // Reuses the canvas's existing marker model: a door is {wallId, t, widthMm},
  // so it rides the wall it belongs to and survives the plan being dragged.
  const openingsBox = host.querySelector<HTMLElement>('[data-role=openings]')!;
  const openingsList = host.querySelector<HTMLElement>('[data-role=openings-list]')!;
  const openingsHint = host.querySelector<HTMLElement>('[data-role=openings-hint]')!;

  // Order the openings were added in, so ↶ Undo can take the last one back.
  // Kept in sync with state rather than only appended to, because openings also
  // arrive by tapping the canvas and leave by re-measuring a wall too short to
  // hold them.
  const openingOrder: Array<{ id: string; kind: "door" | "window" }> = [];

  function syncOpeningOrder(): void {
    const room = ctx.state.room;
    const live = new Map<string, "door" | "window">();
    for (const m of room.doors) live.set(m.id, "door");
    for (const m of room.windows) live.set(m.id, "window");
    for (let i = openingOrder.length - 1; i >= 0; i--) {
      if (!live.has(openingOrder[i]!.id)) openingOrder.splice(i, 1);
    }
    const known = new Set(openingOrder.map((o) => o.id));
    for (const [id, kind] of live) if (!known.has(id)) openingOrder.push({ id, kind });
    openUndoBtn.disabled = openingOrder.length === 0;
  }

  /** How far an opening's near edge sits from its wall's starting corner. The
   *  same measurement the + Add field takes, read back out of {t, widthMm}. */
  function offsetMmOf(m: RoomMarker, walls: Wall[]): number {
    const wall = walls.find((w) => w.id === m.wallId);
    if (!wall) return 0;
    return Math.max(0, Math.round(m.t * wallLength(wall) - m.widthMm / 2));
  }

  /** Wall picker for + Add — labelled to match the numbers on the canvas. */
  function renderWallOptions(): void {
    const walls = ctx.state.room.walls;
    const previous = openWallSel.value;
    openWallSel.innerHTML = walls
      .map((w, i) => `<option value="${escapeAttr(w.id)}">Wall ${i + 1} — ${Math.round(wallLength(w))}mm</option>`)
      .join("");
    if (walls.some((w) => w.id === previous)) openWallSel.value = previous;
  }

  /** + Add needs a tool armed and a measurement typed; nothing is guessed. */
  function syncOpenAdd(): void {
    const tool = canvas.getTool();
    const v = Number(openOffset.value);
    openAddBtn.disabled =
      (tool !== "door" && tool !== "window") ||
      openOffset.value === "" || !Number.isFinite(v) || v < 0;
  }

  let openingsSig = "";

  function renderOpenings(): void {
    const room = ctx.state.room;
    const items = [
      ...room.doors.map((m) => ({ m, kind: "door" as const, label: "Door" })),
      ...room.windows.map((m) => ({ m, kind: "window" as const, label: "Window" })),
    ];
    syncOpeningOrder();
    openingsHint.textContent = items.length === 0
      ? "Tap + Door or + Window and tap the wall it sits on — or pick the wall, type how far it is from that wall's corner, and press + Add."
      // Dragging needs no mode-switch — say so, or the Move button reads as a
      // prerequisite rather than what it is (drag the whole plan).
      : "Drag any door or window along its wall, or set its width and distance from the corner below.";

    const sig = items.map(({ m }) => `${m.id}:${m.wallId}`).join(",");
    if (sig === openingsSig) {
      // Same set of openings — refresh the values in place. Re-rendering here
      // would rip the fields out from under a customer mid-type, and a canvas
      // drag calls straight through to this on every pointer move.
      const byId = new Map(items.map(({ m }) => [m.id, m]));
      for (const input of openingsList.querySelectorAll<HTMLInputElement>("input[data-opening-id]")) {
        if (input === document.activeElement) continue;
        const m = byId.get(input.dataset.openingId ?? "");
        if (!m) continue;
        input.value = String(
          input.dataset.openingField === "width" ? m.widthMm : offsetMmOf(m, room.walls),
        );
      }
      return;
    }
    openingsSig = sig;

    const wallNo = new Map(room.walls.map((w, i) => [w.id, i + 1]));
    openingsList.innerHTML = items
      .map(
        ({ m, kind, label }, i) => `
        <div class="wiz-opening">
          <span class="wiz-opening__kind wiz-opening__kind--${kind}">${label} ${i + 1} · Wall ${wallNo.get(m.wallId) ?? "?"}</span>
          <label class="wiz-opening__field">
            <input class="wiz-num" type="number" inputmode="numeric" min="${MIN_OPENING_MM}"
                   max="${MAX_OPENING_MM}" step="10" data-opening-field="width"
                   data-opening-id="${escapeAttr(m.id)}" value="${m.widthMm}"
                   aria-label="${label} ${i + 1} width in millimetres" />
            <span class="wiz-opening__unit">mm wide</span>
          </label>
          <label class="wiz-opening__field">
            <input class="wiz-num" type="number" inputmode="numeric" min="0"
                   max="${MAX_TPL_WALL_MM}" step="10" data-opening-field="offset"
                   data-opening-id="${escapeAttr(m.id)}" value="${offsetMmOf(m, room.walls)}"
                   aria-label="${label} ${i + 1} distance from the corner in millimetres" />
            <span class="wiz-opening__unit">mm from corner</span>
          </label>
        </div>`,
      )
      .join("");
  }

  openingsList.addEventListener("input", (e) => {
    const input = e.target as HTMLInputElement;
    const id = input.getAttribute("data-opening-id");
    if (!id) return;
    const v = Number(input.value);
    if (!Number.isFinite(v)) return;
    const room = ctx.state.room;
    const marker = [...room.doors, ...room.windows].find((m) => m.id === id);
    if (!marker) return;
    const wall = room.walls.find((w) => w.id === marker.wallId);
    if (!wall) return;
    const len = wallLength(wall);
    if (len < 1) return;

    let next: RoomMarker;
    if (input.getAttribute("data-opening-field") === "offset") {
      // Ignore half-typed values; the field keeps whatever they typed either way.
      if (v < 0) return;
      const maxNear = len - marker.widthMm;
      if (maxNear < 0) return;
      next = { ...marker, t: (Math.min(v, maxNear) + marker.widthMm / 2) / len };
    } else {
      if (v < MIN_OPENING_MM || v > MAX_OPENING_MM || v > len) return;
      // Hold the near edge: widening a door shouldn't slide it down the wall.
      const near = Math.min(Math.max(0, marker.t * len - marker.widthMm / 2), len - v);
      next = { ...marker, widthMm: v, t: (near + v / 2) / len };
    }

    const put = (list: RoomMarker[]): RoomMarker[] =>
      list.map((m) => (m.id === id ? next : m));
    ctx.setState({
      ...ctx.state,
      room: { ...room, doors: put(room.doors), windows: put(room.windows) },
    });
    canvas.rerender();
  });

  openingsBox.querySelector<HTMLElement>(".wiz-openings__tools")!
    .addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-open-tool]");
      if (!btn) return;
      const tool = btn.getAttribute("data-open-tool") as CanvasTool;
      canvas.setTool(tool);
      for (const b of openingsBox.querySelectorAll<HTMLButtonElement>("[data-open-tool]")) {
        b.classList.toggle("is-active", b === btn);
      }
      openingsHint.textContent = tool === "select"
        ? "Drag the plan to reposition it. Doors and windows drag on their own."
        : `Tap the wall where the ${tool} goes, or measure it in: pick the wall, type the distance from its corner and press + Add.`;
      syncOpenAdd();
    });

  // ── Placing an opening by measurement ──
  // The typed millimetres are corner → near edge of the opening (architrave
  // included), read clockwise, exactly as the "How to Measure Your Room"
  // instructions on this page tell the customer to measure.
  openOffset.addEventListener("input", () => {
    if (openOffset.value.length > 5) openOffset.value = openOffset.value.slice(0, 5);
    syncOpenAdd();
  });
  openOffset.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !openAddBtn.disabled) {
      e.preventDefault();
      openAddBtn.click();
    }
  });
  openWallSel.addEventListener("change", () => canvas.setHighlight(openWallSel.value || null));
  openWallSel.addEventListener("focus", () => canvas.setHighlight(openWallSel.value || null));
  openWallSel.addEventListener("blur", () => canvas.setHighlight(null));

  openAddBtn.addEventListener("click", () => {
    const tool = canvas.getTool();
    if (tool !== "door" && tool !== "window") return;
    const room = ctx.state.room;
    const wall = room.walls.find((w) => w.id === openWallSel.value);
    if (!wall) return;
    const len = wallLength(wall);
    const widthMm = DEFAULT_WIDTH_MM[tool];
    const dist = Number(openOffset.value);
    if (!Number.isFinite(dist) || dist < 0) return;

    const maxNear = len - widthMm;
    if (maxNear < 0) {
      toast(openAddBtn, `Wall is under ${widthMm}mm`);
      return;
    }
    if (dist > maxNear) toast(openAddBtn, `Wall ends — placed at ${Math.round(maxNear)}mm`);
    const near = Math.min(dist, maxNear);
    const marker: RoomMarker = {
      id: makeId(),
      kind: tool,
      wallId: wall.id,
      t: (near + widthMm / 2) / len,
      widthMm,
    };
    ctx.setState({
      ...ctx.state,
      room: tool === "door"
        ? { ...room, doors: [...room.doors, marker] }
        : { ...room, windows: [...room.windows, marker] },
    });
    canvas.rerender();
    renderOpenings();
    openOffset.value = "";
    syncOpenAdd();
    openOffset.focus();
  });

  openUndoBtn.addEventListener("click", () => {
    const last = openingOrder.pop();
    if (!last) return;
    const room = ctx.state.room;
    ctx.setState({
      ...ctx.state,
      room: {
        ...room,
        doors: room.doors.filter((m) => m.id !== last.id),
        windows: room.windows.filter((m) => m.id !== last.id),
      },
    });
    canvas.rerender();
    renderOpenings();
  });

  // ── Per-wall lengths ──
  tplParams.addEventListener("input", (e) => {
    const input = e.target as HTMLInputElement;
    const raw = input.getAttribute("data-tpl-wall");
    if (raw === null || !activeTemplate) return;
    const i = Number(raw);
    if (!Number.isInteger(i) || i < 0 || i >= tplLengths.length) return;
    const v = Number(input.value);
    // Ignore mid-typing junk and out-of-range lengths; the plan keeps its last
    // good shape rather than collapsing mid-keystroke, and the field keeps
    // whatever they typed either way. Walls are independent, so only this one
    // moves — nothing else to re-solve or repaint.
    if (!wallLengthIsValid(v)) return;
    tplLengths[i] = v;
    placeTemplate(true);
  });

  // Focusing a wall's field lights that wall up on the canvas, so "Wall 5" is
  // never a guess.
  tplParams.addEventListener("focusin", (e) => {
    const input = (e.target as HTMLElement).closest<HTMLInputElement>("[data-tpl-wall]");
    canvas.setHighlight(input ? `tpl-${input.dataset.tplWall}` : null);
  });
  tplParams.addEventListener("focusout", () => canvas.setHighlight(null));

  // ── Next ──
  nextBtn.addEventListener("click", () => ctx.goTo(1));

  // ── Bottom-bar Save / Chat / Login / Next ──
  const bbSave = host.querySelector<HTMLButtonElement>('[data-action=save]');
  const bbChat = host.querySelector<HTMLButtonElement>('[data-action=chat]');
  const bbNext = host.querySelector<HTMLButtonElement>('.wiz-bottombar [data-action=next]');

  // Login → "Start planning or log in" popup (Bug 9), intercepted so it doesn't
  // navigate to the dead /customer-portal path. Once step 1 has captured the
  // customer this control is a Logout instead — see authBarButtonHtml.
  wireAuthBarButton(host, ctx);

  bbSave?.addEventListener("click", () => {
    try {
      const payload = {
        retailerId: ctx.state.retailerId,
        room: ctx.state.room,
        savedAt: new Date().toISOString(),
      };
      window.localStorage.setItem("tkos-wiz-save-v1", JSON.stringify(payload));
      toast(bbSave, "Saved ✓");
    } catch {
      toast(bbSave, "Couldn't save — storage full?");
    }
  });

  bbChat?.addEventListener("click", () => openChatPopup(ctx));

  bbNext?.addEventListener("click", () => ctx.goTo(1));

  // ── Initial paint ──
  setTool("wall");
  renderDirection();
  updateCanvasCursor();
  renderSegments();
  renderCounts();
  syncUndoButton();
  syncAddButton();
  renderUploads();
  renderWallOptions();
  renderOpenings();
  syncOpenAdd();
  // Last: applyEntryMode overrides the tool for the templates panel.
  applyEntryMode();
}

function seedPenFromRoom(room: RoomDimensions): { x: number; y: number } {
  const last = room.walls[room.walls.length - 1];
  return last ? { x: last.x2, y: last.y2 } : { x: 0, y: 0 };
}

function makeId(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

function escapeText(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeAttr(s: string): string {
  return escapeText(s).replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

/** Temporary inline confirmation on a button — shows `msg` for ~1.4s. */
function toast(btn: HTMLElement, msg: string): void {
  const orig = btn.getAttribute("data-toast-orig") ?? btn.innerHTML;
  btn.setAttribute("data-toast-orig", orig);
  btn.innerHTML = `<span>${msg}</span>`;
  window.setTimeout(() => { btn.innerHTML = orig; }, 1400);
}
